import { Input, Loader, Select, SelectOption } from '@shared/UI';
import { getSelectValue } from '@widgets/AccountsTable/components/AccountsViewDrawer/utils/getSelectValue';
import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BiEditAlt, BiSearch } from 'react-icons/bi';
import './Preferences.scss';
import 'chart.js/auto';
import {
    AgeOptions,
    FinancialGoalsOptions,
    InvestmentExperienceOptions,
    ModelVersionOptions,
    ReturnExpectationsOptions,
    RiskToleranceOptions,
    TimeHorizonOptions,
} from './consts';
import { useParams } from 'react-router-dom';
import { get, upsert } from './api';
import { getValueFromEnum } from '@widgets/AccountsTable/components/utils';
import {
    Age,
    FinancialGoals,
    InvestmentExperience,
    ModelVersion,
    ReturnExpectations,
    RiskTolerance,
    TimeHorizon,
} from './interfaces';
import { useModal } from '@features/Modal';
import { AuditsViewModal } from './components/PreferencesViewModal';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    IconButton,
    InputAdornment,
    OutlinedInput,
    TextField,
} from '@mui/material';
import InvestmentPortfolio from './components/InvestmentPortfolio/InvestmentPortfolio';
import { FaCaretDown, FaCaretUp } from 'react-icons/fa';
import { BlackoutPeriodModal } from './components/BlackoutPeriodModal';
import InvestmentTable from './components/InvestmentTable/InvestmentTable';
import moment from 'moment-timezone';
import { useNotify } from '@features/Notify';
import { BuySellAdjustmentModal } from './components/BuySellAdjustmentModal';

interface AccountsInfoProps {
    fieldName: string;
    value: string | number | null;
    className?: string;
    shouldSplit?: boolean;
    editableField?: 'select' | 'input';
    keyName?: string;
    options?: SelectOption[];
    onChange?: (key: string, value: string | boolean | number) => void;
}

interface PreferencesData {
    ageRange: string;
    investmentExperience: string;
    financialGoals: string;
    timeHorizon: string;
    riskTolerance: string;
    returnExpectation: string;
    modelVersion: string;
    minCashReserve: string;
    taxBracket: string;
    threshold: string;
    sectorExclusions: string[];
    // etc.
}

const DEFAULT_PREFERENCES: PreferencesData = {
    ageRange: '',
    investmentExperience: '',
    financialGoals: '',
    timeHorizon: '',
    riskTolerance: '',
    returnExpectation: '',
    modelVersion: '',
    minCashReserve: '0',
    taxBracket: '0',
    threshold: '0',
    sectorExclusions: [],
};

const AccountsInfo: React.FC<AccountsInfoProps> = (props) => {
    const { fieldName, value, className, editableField, onChange, keyName, options } = props;
    const userStorage = localStorage.getItem('User');
    const userObject = useMemo(() => JSON.parse(userStorage || '{}'), [userStorage]);

    const [openEdit, setOpenEdit] = useState(false);

    const renderEditableField = useMemo(() => {
        if (!openEdit || !keyName) {
            return value;
        }

        switch (editableField) {
            case 'input':
                return (
                    <Input
                        editable
                        value={value?.toString()}
                        onChange={(e) => onChange?.(keyName, e.target.value)}
                    />
                );
            case 'select':
                return (
                    <Select
                        currentValue={getSelectValue(fieldName, value ? value?.toString() : '').toString()}
                        onSelect={(value) => {
                            onChange?.(value!, value);
                        }}
                        options={options || []}
                    />
                );
            default:
                return value;
        }
    }, [openEdit, value]);

    return (
        <div className={classNames('widget-preferences-status-table-drawer__info-item', className)}>
            <span
                data-tooltip-id={fieldName}
                className='widget-trade-status-table-drawer__info-header'
            >
                {fieldName}: {''}
            </span>
            <span className={classNames('widget-trade-status-table-drawer__info-content', { nullable: !value })}>
                {keyName === 'minCashReserve' && '$'}
                {renderEditableField}
                {(keyName === 'taxBracket' || keyName === 'threshold') && '%'}
                {userObject?.userRole !== 5 && editableField && (
                    <BiEditAlt onClick={() => setOpenEdit((prev) => !prev)} />
                )}
            </span>
        </div>
    );
};

export const Preferences: React.FC = () => {
    const userStorage = localStorage.getItem('User');
    const userObject = useMemo(() => JSON.parse(userStorage || '{}'), [userStorage]);
    const account = userObject?.accounts[0];
    const { userId } = useParams<{ userId: string }>();
    const [isLoading, setIsLoading] = useState(false);
    const [list, setList] = useState<any[]>([]);
    const [blackoutPeriodlist, setBlackoutPeriodlist] = useState<any[]>([]);
    const [answers, setAnswers] = useState<PreferencesData>(DEFAULT_PREFERENCES);
    const [investment, setInvestment] = useState<any>();

    const [sectorExclusions, setSectorExclusions] = useState<string[]>(answers?.sectorExclusions || []);
    const [isSectorOpen, setIsSectorOpen] = useState(false);
    const [isRebalanced, setIsRebalanced] = useState(true);
    const [searchSectorText, setSearchSectorText] = useState('');
    const sectorSearchInputRef = useRef(null);
    const [investments, setInvestments] = useState<any[]>([]);
    const [cash, setCash] = useState(0);
    const [totalInvestment, setTotalInvestment] = useState(0);
    const notify = useNotify();

    const [initialValues, setInitialValues] = useState({
        answers: DEFAULT_PREFERENCES as PreferencesData | null,
        sectorExclusions: [] as string[],
        blackoutPeriodlist: [] as any[],
        list: [] as any[],
    });

    const handleUpdatePreferences = async () => {
        try {
            const toUtcString = (date: any) => new Date(date).toISOString(); // e.g. "2025-05-16T10:00:00.000Z"

            const formattedBlackoutPeriodlist = blackoutPeriodlist.map((period) => ({
                ...period,
                symbol: Array.isArray(period.symbol) ? period.symbol.join(',') : period.symbol,
                startDate: toUtcString(period.startDate),
                endDate: toUtcString(period.endDate),
            }));

            const updatedData = {
                userId,
                preferences: answers,
                sectorExclusions,
                blackoutPeriodlist: formattedBlackoutPeriodlist,
                investmentModel: list,
            };

            var result = await upsert(updatedData);
            notify.success(result);
            getPreferences();
        } catch (error) {
            notify.error(error ? error.toString() : 'Failed');
        }
    };

    const sectorOptions: string[] = [
        'Aerospace & Defense',
        'Airlines',
        'Auto Components',
        'Automobiles',
        'Banking',
        'Beverages',
        'Biotechnology',
        'Building',
        'Chemicals',
        'Commercial Services & Supplies',
        'Communications',
        'Construction',
        'Consumer products',
        'Distributors',
        'Diversified Consumer Services',
        'Electrical Equipment',
        'Energy',
        'Financial Services',
        'Food Products',
        'Health Care',
        'Hotels, Restaurants & Leisure',
        'Industrial Conglomerates',
        'Insurance',
        'Leisure Products',
        'Life Sciences Tools & Services',
        'Logistics & Transportation',
        'Machinery',
        'Marine',
        'Media',
        'Metals & Mining',
        'N/A',
        'Packaging',
        'Paper & Forest',
        'Pharmaceuticals',
        'Professional Services',
        'Real Estate',
        'Retail',
        'Road & Rail',
        'Semiconductors',
        'Technology',
        'Telecommunication',
        'Textiles, Apparel & Luxury Goods',
        'Tobacco',
        'Trading Companies & Distributors',
        'Transportation Infrastructure',
        'Unknown',
        'Utilities',
    ];

    const toggleSectorDropdown = () => setIsSectorOpen(!isSectorOpen);

    const handleSectorToggle = (sector: string) => {
        setSectorExclusions((prev) => (prev.includes(sector) ? prev.filter((s) => s !== sector) : [...prev, sector]));
    };

    const getPreferences = async () => {
        try {
            setIsLoading(true);
            let req = await get(userObject.userRole === 5 ? userObject.id : Number(userId));
            setAnswers({
                ...DEFAULT_PREFERENCES,
                ...req?.preferences,
            });
            setIsRebalanced(req?.isRebalanced);
            setInvestment(req?.investments);
            setList(req?.investments);
            setBlackoutPeriodlist(req?.blackoutPeriods);
            setSectorExclusions(req?.preferences?.sectorExclusions || []);
            setInvestments(req.investmentBreakdowns);
            setTotalInvestment(req.investment);
            setCash(req.cash);
            setInitialValues({
                answers: req?.preferences,
                sectorExclusions: req?.preferences?.sectorExclusions || [],
                blackoutPeriodlist: req?.blackoutPeriods || [],
                list: req?.investments || [],
            });
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
        }
    };

    const hasChanges = useMemo(() => {
        // Check if answers object has changed
        const answersChanged = Object.keys(answers || {}).some(
            (key) =>
                JSON.stringify(answers?.[key as keyof PreferencesData]) !==
                JSON.stringify(initialValues.answers?.[key as keyof PreferencesData]),
        );

        // Check if sectorExclusions has changed
        const sectorExclusionsChanged =
            JSON.stringify(sectorExclusions) !== JSON.stringify(initialValues.sectorExclusions);

        // Check if blackoutPeriodlist has changed
        const blackoutPeriodlistChanged =
            JSON.stringify(blackoutPeriodlist) !== JSON.stringify(initialValues.blackoutPeriodlist);

        // Check if list (investments) has changed
        const listChanged = JSON.stringify(list) !== JSON.stringify(initialValues.list);

        return answersChanged || sectorExclusionsChanged || blackoutPeriodlistChanged || listChanged;
    }, [answers, sectorExclusions, blackoutPeriodlist, list, initialValues]);

    useEffect(() => {
        if (userObject.id) {
            getPreferences();
        }
    }, [userObject.id]);

    const onChange = <K extends keyof PreferencesData>(key: K, value: PreferencesData[K]) => {
        setAnswers((prev) => ({
            ...(prev || DEFAULT_PREFERENCES),
            [key]: value,
        }));
    };

    const { open: openBlackoutViewModal } = useModal(BlackoutPeriodModal);
    const [showSectorDialog, setShowSectorDialog] = useState(false);
    const [showBlackoutDialog, setShowBlackoutDialog] = useState(false);

    const onBlackoutViewClick = () => {
        openBlackoutViewModal({ list: blackoutPeriodlist, setList: setBlackoutPeriodlist });
    };

    const { open: openAuditsViewModal } = useModal(AuditsViewModal);

    const onAuditViewClick = () => {
        openAuditsViewModal({ list, setList });
    };

    const { open: openBuySellAdjustmentModal } = useModal(BuySellAdjustmentModal);

    const onBuySellAdjustmentClick = () => {
        openBuySellAdjustmentModal({ userId: Number(userId) });
    };

    const handleValueChange = (key: string, value: string | number | boolean | null) => {
        setAnswers((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                [key]: value === '' ? '0' : value,
            };
        });
    };

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div>
            <h1
                className='account-details-header'
                style={{ display: 'flex', marginTop: '10px' }}
            >
                Account Preferences
                <div style={{ marginLeft: 'auto', marginRight: '10px', display: 'flex', gap: '10px' }}>
                    {hasChanges && (
                        <Button
                            variant='contained'
                            color='primary'
                            onClick={handleUpdatePreferences}
                            className='save-model-button'
                        >
                            Save Updated Model
                        </Button>
                    )}
                    {!isRebalanced && userObject?.userRole !== 5 && (
                        <div className='rebalance-button'>
                            <div
                                className={`tooltip-wrapper ${investment.length === 0 || cash < parseFloat(answers.minCashReserve) ? 'disabled' : ''}`}
                            >
                                <button
                                    onClick={onBuySellAdjustmentClick}
                                    disabled={investment.length === 0 || cash < parseFloat(answers.minCashReserve)}
                                    className='update-preferences-button'
                                >
                                    Rebalance
                                </button>
                                {investment.length === 0 && (
                                    <span className='tooltip-text'>
                                        Please create investment model to do rebalancing
                                    </span>
                                )}
                                {cash < parseFloat(answers.minCashReserve) && (
                                    <span className='tooltip-text'>
                                        Please add cash to your account to meet the required minimum cash reserve.
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </h1>
            <div className='account-details-border' />

            {userObject?.userRole !== 5 && (
                <div
                    style={{
                        display: 'flex',
                        margin: '20px',
                        background: 'white',
                        borderRadius: '10px',
                        border: '1px solid #E5E7EB',
                    }}
                >
                    {/* General Information */}
                    <div className='preference-info-card'>
                        <div className='investment-header'>
                            <div className='header-title'>Investment Model</div>
                            {
                                <div style={{ marginLeft: 'auto' }}>
                                    <Button
                                        variant='contained'
                                        color='primary'
                                        onClick={onAuditViewClick}
                                        className='update-modal-button'
                                    >
                                        {list?.length > 0 ? 'Update Model' : 'Create Model'}
                                    </Button>
                                </div>
                            }
                        </div>
                        {list.length > 0 && (
                            <InvestmentPortfolio
                                investments={list || []}
                                fromUpdate={false}
                            />
                        )}
                        {list.length < 1 && (
                            <div className='no-investment-portfolio'>
                                <div className='investment-portfolio-wrapper'>
                                    <InvestmentPortfolio
                                        investments={[
                                            { symbol: 'TSLA', allocationPercent: 60, preserved: true },
                                            { symbol: 'AVGO', allocationPercent: 20, preserved: true },
                                            { symbol: 'Cash', allocationPercent: 20, preserved: true },
                                        ]}
                                        fromUpdate={false}
                                    />
                                    <div className='portfolio-overlay'>
                                        <span
                                            className='get-started-link'
                                            onClick={onAuditViewClick}
                                        >
                                            Get started
                                        </span>{' '}
                                        by creating a new model to define asset allocations and preferences.
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className='preference-info-card-table'>
                        <InvestmentTable
                            investments={investments}
                            totalCash={cash}
                            totalInvestment={totalInvestment}
                        />
                    </div>
                </div>
            )}

            {account ? (
                <div>
                    {userObject?.userRole === 5 && (
                        <div style={{ display: 'flex', padding: '20px', gap: '20px' }}>
                            {/* General Information */}
                            {userObject?.userRole === 5 && (
                                <div className='preference-info-card-client'>
                                    <div className='preferences-info-header'>Account preferences details</div>
                                    <div className='preference-info-content'>
                                        <AccountsInfo
                                            value={getValueFromEnum(answers?.ageRange ?? '', Age)}
                                            onChange={(value) => onChange('ageRange', value)}
                                            fieldName='Age'
                                            keyName="What's your age?"
                                            options={AgeOptions}
                                            editableField={'select'}
                                        />
                                        <AccountsInfo
                                            value={getValueFromEnum(
                                                answers?.investmentExperience ?? '',
                                                InvestmentExperience,
                                            )}
                                            onChange={(value) => onChange('investmentExperience', value)}
                                            fieldName='Investment experience'
                                            keyName='What’s your investment experience?'
                                            options={InvestmentExperienceOptions}
                                            editableField={'select'}
                                        />
                                        <AccountsInfo
                                            value={getValueFromEnum(answers?.financialGoals ?? '', FinancialGoals)}
                                            onChange={(value) => onChange('financialGoals', value)}
                                            fieldName='Financial goal'
                                            keyName='What’s your financial goal?'
                                            options={FinancialGoalsOptions}
                                            editableField={'select'}
                                        />
                                        <AccountsInfo
                                            value={getValueFromEnum(answers?.timeHorizon ?? '', TimeHorizon)}
                                            onChange={(value) => onChange('timeHorizon', value)}
                                            fieldName='Preferred time horizon for investment'
                                            keyName='Preferred time horizon for investment?'
                                            options={TimeHorizonOptions}
                                            editableField={'select'}
                                        />
                                        <AccountsInfo
                                            value={getValueFromEnum(answers?.riskTolerance ?? '', RiskTolerance)}
                                            onChange={(value) => onChange('riskTolerance', value)}
                                            fieldName='Risk tolerance'
                                            keyName='Risk tolerance?'
                                            options={RiskToleranceOptions}
                                            editableField={'select'}
                                        />
                                        <AccountsInfo
                                            value={getValueFromEnum(
                                                answers?.returnExpectation ?? '',
                                                ReturnExpectations,
                                            )}
                                            onChange={(value) => onChange('returnExpectation', value)}
                                            fieldName='Return expectations'
                                            keyName='Return expectations?'
                                            options={ReturnExpectationsOptions}
                                            editableField={'select'}
                                        />
                                        <AccountsInfo
                                            value={getValueFromEnum(answers?.modelVersion ?? '', ModelVersion)}
                                            onChange={(value) => onChange('modelVersion', value)}
                                            fieldName='Model Version'
                                            keyName='Model Version?'
                                            options={ModelVersionOptions}
                                            editableField={'select'}
                                        />
                                        <AccountsInfo
                                            value={answers?.minCashReserve?.toString() || '0'}
                                            fieldName='Minimum Cash Reserve'
                                            editableField='input'
                                            onChange={(value) => onChange('minCashReserve', value)}
                                            keyName='minCashReserve'
                                        />

                                        <div className='div-flex-border-bottom'>
                                            <div className='sector-div'>Sector Exclusions </div>
                                            {userObject?.userRole !== 5 && (
                                                <div>
                                                    <FormControl className='form-control-full-width'>
                                                        <div className='dropdown-container'>
                                                            <OutlinedInput
                                                                value={
                                                                    sectorExclusions.length > 0
                                                                        ? `${sectorExclusions.length} Sectors Selected`
                                                                        : 'Select Sectors'
                                                                }
                                                                disabled
                                                                className='outlined-input-style'
                                                                endAdornment={
                                                                    <InputAdornment position='end'>
                                                                        <IconButton
                                                                            edge='end'
                                                                            onClick={toggleSectorDropdown}
                                                                            className='icon-button-style-caret'
                                                                        >
                                                                            {isSectorOpen ? (
                                                                                <FaCaretUp />
                                                                            ) : (
                                                                                <FaCaretDown />
                                                                            )}
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                }
                                                            />
                                                            {isSectorOpen && (
                                                                <div className='dropdown-menu'>
                                                                    <Box
                                                                        className='padding-vertical-3 padding-horizontal-10'
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        <div className='state-select-container'>
                                                                            <div>Select Sectors</div>
                                                                            <Button
                                                                                variant='text'
                                                                                color='primary'
                                                                                onClick={() => setSectorExclusions([])}
                                                                                size='small'
                                                                                className='small-button'
                                                                            >
                                                                                Clear all
                                                                            </Button>
                                                                        </div>
                                                                        <TextField
                                                                            placeholder='Search Sectors'
                                                                            variant='outlined'
                                                                            size='small'
                                                                            fullWidth
                                                                            value={searchSectorText}
                                                                            onChange={(e) => {
                                                                                e.stopPropagation();
                                                                                setSearchSectorText(e.target.value);
                                                                            }}
                                                                            inputRef={sectorSearchInputRef}
                                                                            onKeyDown={(e) => e.stopPropagation()}
                                                                            className='dropdown-search-field'
                                                                            InputProps={{
                                                                                startAdornment: (
                                                                                    <InputAdornment position='start'>
                                                                                        <BiSearch />
                                                                                    </InputAdornment>
                                                                                ),
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                    <div className='filter-content'>
                                                                        {sectorOptions
                                                                            .filter((sector) =>
                                                                                sector
                                                                                    .toLowerCase()
                                                                                    .includes(
                                                                                        searchSectorText.toLowerCase(),
                                                                                    ),
                                                                            )
                                                                            .map((sector) => (
                                                                                <div
                                                                                    key={sector}
                                                                                    className='filter-option'
                                                                                >
                                                                                    <Checkbox
                                                                                        onClick={() =>
                                                                                            handleSectorToggle(sector)
                                                                                        }
                                                                                        checked={sectorExclusions.includes(
                                                                                            sector,
                                                                                        )}
                                                                                    />
                                                                                    <span>{sector}</span>
                                                                                </div>
                                                                            ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </FormControl>
                                                </div>
                                            )}
                                            {userObject?.userRole === 5 && (
                                                <div className='sector-exclusions-grid'>
                                                    <button
                                                        className='sector-exclusions-detail-button'
                                                        onClick={() => setShowSectorDialog(true)}
                                                    >
                                                        Details
                                                    </button>
                                                </div>
                                            )}
                                            {showSectorDialog && (
                                                <div className='sector-dialog-overlay'>
                                                    <div className='sector-dialog'>
                                                        <div className='sector-dialog__header'>
                                                            <h2>Sector Exclusions</h2>
                                                            <button
                                                                className='sector-dialog__close'
                                                                onClick={() => setShowSectorDialog(false)}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>

                                                        <div className='sector-dialog__content'>
                                                            {sectorExclusions.map((sector, index) => (
                                                                <span
                                                                    key={index}
                                                                    className='sector-tag'
                                                                >
                                                                    {sector}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <AccountsInfo
                                            value={String(answers?.taxBracket)}
                                            fieldName='Tax Bracket'
                                            editableField='input'
                                            onChange={(value) => onChange('taxBracket', value)}
                                            keyName='taxBracket'
                                        />
                                        <AccountsInfo
                                            value={answers ? String(answers.threshold) : ''}
                                            fieldName='Threshold'
                                            editableField='input'
                                            onChange={(value) => onChange('threshold', value)}
                                            keyName='threshold'
                                        />
                                        <div className='div-flex-border-bottom'>
                                            <div className='sector-div'>Blackout Period </div>
                                            <div className='blackout-period-list'>
                                                <button
                                                    className='sector-exclusions-detail-button'
                                                    onClick={() => setShowBlackoutDialog(true)}
                                                >
                                                    Details
                                                </button>
                                            </div>
                                            {showBlackoutDialog && (
                                                <div className='sector-dialog-overlay'>
                                                    <div className='sector-dialog'>
                                                        <div className='sector-dialog__header'>
                                                            <h2>Blackout Period</h2>
                                                            <button
                                                                className='sector-dialog__close'
                                                                onClick={() => setShowBlackoutDialog(false)}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>

                                                        {blackoutPeriodlist?.map((item: any, index: any) => (
                                                            <div
                                                                key={index}
                                                                className='blackout-period-box'
                                                            >
                                                                <div className='symbol'>{item.symbol}</div>
                                                                <div className='date-range'>
                                                                    {moment(item.startDate).format('M/D/YYYY')}{' '}
                                                                    <span className='between'>To</span>{' '}
                                                                    {moment(item.endDate).format('M/D/YYYY')}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {userObject?.userRole !== 5 && (
                                                <BiEditAlt
                                                    className='bi-edit-alt-icon'
                                                    onClick={onBlackoutViewClick}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {userObject?.userRole === 5 && (
                                <div className='preference-info-card-investment-modal'>
                                    <div className='preferences-info-header'>Investment Model</div>
                                    <InvestmentPortfolio
                                        investments={investment || []}
                                        fromUpdate={false}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    <div
                        style={{
                            display: 'flex',
                            margin: '20px',
                            border: '1px solid #E5E7EB',
                            borderRadius: '10px',
                            background: 'white',
                        }}
                    >
                        {userObject?.userRole !== 5 && (
                            <div className='preference-info-card'>
                                <div className='preferences-info-header'>Account preferences details</div>
                                <div className='preference-info-content'>
                                    <AccountsInfo
                                        value={getValueFromEnum(answers?.ageRange ?? '', Age)}
                                        onChange={(value) => onChange('ageRange', value)}
                                        fieldName='Age'
                                        keyName="What's your age?"
                                        options={AgeOptions}
                                        editableField={'select'}
                                    />
                                    <AccountsInfo
                                        value={getValueFromEnum(
                                            answers?.investmentExperience ?? '',
                                            InvestmentExperience,
                                        )}
                                        onChange={(value) => onChange('investmentExperience', value)}
                                        fieldName='Investment experience'
                                        keyName='What’s your investment experience?'
                                        options={InvestmentExperienceOptions}
                                        editableField={'select'}
                                    />
                                    <AccountsInfo
                                        value={getValueFromEnum(answers?.financialGoals ?? '', FinancialGoals)}
                                        onChange={(value) => onChange('financialGoals', value)}
                                        fieldName='Financial goal'
                                        keyName='What’s your financial goal?'
                                        options={FinancialGoalsOptions}
                                        editableField={'select'}
                                    />
                                    <AccountsInfo
                                        value={getValueFromEnum(answers?.timeHorizon ?? '', TimeHorizon)}
                                        onChange={(value) => onChange('timeHorizon', value)}
                                        fieldName='Preferred time horizon for investment'
                                        keyName='Preferred time horizon for investment?'
                                        options={TimeHorizonOptions}
                                        editableField={'select'}
                                    />
                                    <AccountsInfo
                                        value={getValueFromEnum(answers?.riskTolerance ?? '', RiskTolerance)}
                                        onChange={(value) => onChange('riskTolerance', value)}
                                        fieldName='Risk tolerance'
                                        keyName='Risk tolerance?'
                                        options={RiskToleranceOptions}
                                        editableField={'select'}
                                    />
                                    <AccountsInfo
                                        value={getValueFromEnum(answers?.returnExpectation ?? '', ReturnExpectations)}
                                        onChange={(value) => onChange('returnExpectation', value)}
                                        fieldName='Return expectations'
                                        keyName='Return expectations?'
                                        options={ReturnExpectationsOptions}
                                        editableField={'select'}
                                    />
                                </div>
                            </div>
                        )}
                        {userObject?.userRole !== 5 && (
                            <div className='preference-info-card'>
                                <div className='preferences-info-header'></div>
                                <div className='preference-info-content'>
                                    <AccountsInfo
                                        value={getValueFromEnum(answers?.modelVersion ?? '', ModelVersion)}
                                        onChange={(value) => onChange('modelVersion', value)}
                                        fieldName='Model Version'
                                        keyName='Model Version?'
                                        options={ModelVersionOptions}
                                        editableField={'select'}
                                    />
                                    <AccountsInfo
                                        value={answers?.minCashReserve?.toString() || '0'}
                                        fieldName='Minimum Cash Reserve'
                                        editableField='input'
                                        onChange={handleValueChange}
                                        keyName='minCashReserve'
                                    />

                                    <div className='div-flex-border-bottom'>
                                        <div className='sector-div'>Sector Exclusions </div>
                                        {userObject?.userRole !== 5 && (
                                            <div>
                                                <FormControl className='form-control-full-width'>
                                                    <div className='dropdown-container'>
                                                        <OutlinedInput
                                                            value={
                                                                sectorExclusions.length > 0
                                                                    ? `${sectorExclusions.length} Sectors Selected`
                                                                    : 'Select Sectors'
                                                            }
                                                            disabled
                                                            className='outlined-input-style'
                                                            endAdornment={
                                                                <InputAdornment position='end'>
                                                                    <IconButton
                                                                        edge='end'
                                                                        onClick={toggleSectorDropdown}
                                                                        className='icon-button-style-caret'
                                                                    >
                                                                        {isSectorOpen ? <FaCaretUp /> : <FaCaretDown />}
                                                                    </IconButton>
                                                                </InputAdornment>
                                                            }
                                                        />
                                                        {isSectorOpen && (
                                                            <div className='dropdown-menu'>
                                                                <Box
                                                                    className='padding-vertical-3 padding-horizontal-10'
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <div className='state-select-container'>
                                                                        <div>Select Sectors</div>
                                                                        <Button
                                                                            variant='text'
                                                                            color='primary'
                                                                            onClick={() => setSectorExclusions([])}
                                                                            size='small'
                                                                            className='small-button'
                                                                        >
                                                                            Clear all
                                                                        </Button>
                                                                    </div>
                                                                    <TextField
                                                                        placeholder='Search Sectors'
                                                                        variant='outlined'
                                                                        size='small'
                                                                        fullWidth
                                                                        value={searchSectorText}
                                                                        onChange={(e) => {
                                                                            e.stopPropagation();
                                                                            setSearchSectorText(e.target.value);
                                                                        }}
                                                                        inputRef={sectorSearchInputRef}
                                                                        onKeyDown={(e) => e.stopPropagation()}
                                                                        className='dropdown-search-field'
                                                                        InputProps={{
                                                                            startAdornment: (
                                                                                <InputAdornment position='start'>
                                                                                    <BiSearch />
                                                                                </InputAdornment>
                                                                            ),
                                                                        }}
                                                                    />
                                                                </Box>
                                                                <div className='filter-content'>
                                                                    {sectorOptions
                                                                        .filter((sector) =>
                                                                            sector
                                                                                .toLowerCase()
                                                                                .includes(
                                                                                    searchSectorText.toLowerCase(),
                                                                                ),
                                                                        )
                                                                        .map((sector) => (
                                                                            <div
                                                                                key={sector}
                                                                                className='filter-option'
                                                                            >
                                                                                <Checkbox
                                                                                    onClick={() =>
                                                                                        handleSectorToggle(sector)
                                                                                    }
                                                                                    checked={sectorExclusions.includes(
                                                                                        sector,
                                                                                    )}
                                                                                />
                                                                                <span>{sector}</span>
                                                                            </div>
                                                                        ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </FormControl>
                                            </div>
                                        )}
                                        {userObject?.userRole === 5 && (
                                            <div className='sector-exclusions-grid'>
                                                {sectorExclusions.map((sector, index) => (
                                                    <span
                                                        key={index}
                                                        className='sector-exclusion-items'
                                                    >
                                                        {sector}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <AccountsInfo
                                        value={answers ? String(answers.taxBracket) : ''}
                                        fieldName='Tax Bracket'
                                        editableField='input'
                                        onChange={handleValueChange}
                                        keyName='taxBracket'
                                    />

                                    <AccountsInfo
                                        value={answers ? String(answers.threshold) : ''}
                                        fieldName='Threshold'
                                        editableField='input'
                                        onChange={handleValueChange}
                                        keyName='threshold'
                                    />

                                    <div className='div-flex-border-bottom'>
                                        <div className='sector-div'>Blackout Period </div>
                                        <div className='blackout-period-list'>
                                            <button
                                                className='sector-exclusions-detail-button'
                                                onClick={() => setShowBlackoutDialog(true)}
                                            >
                                                Details
                                            </button>
                                        </div>
                                        {showBlackoutDialog && (
                                            <div className='sector-dialog-overlay'>
                                                <div className='sector-dialog'>
                                                    <div className='sector-dialog__header'>
                                                        <h2>Blackout Period</h2>
                                                        <button
                                                            className='sector-dialog__close'
                                                            onClick={() => setShowBlackoutDialog(false)}
                                                        >
                                                            ×
                                                        </button>
                                                    </div>

                                                    {blackoutPeriodlist?.map((item: any, index: any) => (
                                                        <div
                                                            key={index}
                                                            className='blackout-period-box'
                                                        >
                                                            <div className='symbol'>{item.symbol}</div>
                                                            <div className='date-range'>
                                                                {moment(item.startDate).format('M/D/YYYY')}{' '}
                                                                <span className='between'>To</span>{' '}
                                                                {moment(item.endDate).format('M/D/YYYY')}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {userObject?.userRole !== 5 && (
                                            <BiEditAlt
                                                className='bi-edit-alt-icon'
                                                onClick={onBlackoutViewClick}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <p>No account information available.</p>
            )}
        </div>
    );
};
